/**
 * The machines of a line in the order of its flow, and the parsers that turn
 * the responses of the machine statements into per-machine values.
 *
 * The line tabs (downtime, rejects, MTBF, faults) have no line statements of
 * their own: they ask each machine of the line with the machine statements
 * and show the machines side by side, in line order. These helpers are pure,
 * so the rules (positions, output machine, shift filter) are unit-tested.
 */

import type { Machine } from "../../services/BackendDataService";
import { applyFlowLayout } from "../../services/FlowLayoutService";

/** A machine at its place in the line. */
export interface LineStation {
  /** Serial number, the `serialno` of the machine statements. */
  sn: string;
  /** Name as the cockpit shows it elsewhere ("Packer (20056)"). */
  name: string;
  /** "1", "2", … in flow order; parallel machines share a number: "2a", "2b". */
  position: string;
  /** 0-based index of the machine's flow group in the line. */
  stage: number;
  /** "2a · Packer (20056)". */
  label: string;
  /** The line output is counted at this machine. */
  isOutput: boolean;
  /** Machine type (bautyp), for its machine-type targets. */
  type: string;
}

/** A machine on a chart axis or in a legend, ★ for the output machine. */
export const stationAxisLabel = (s: LineStation): string =>
  s.isOutput ? `${s.label} ★` : s.label;

/** The serial number the statements take for a machine. */
export const machineSn = (m: Machine): string => m.maschinennummer || m.maschineBK || "";

/**
 * The machines in line order. Flow groups (`flowGroup`, saved by the flow
 * editor) are the stages, ordered by their first machine's `flowOrder`;
 * machines of one group work in parallel and get a letter.
 *
 * The output machine is `outputMachineId` (the line configuration's choice)
 * when it is one of `machines`; without a choice it is every machine of the
 * highest flow group, the rule of the PC.line_output statements.
 */
export function lineStations(
  lineId: string,
  machines: Machine[],
  outputMachineId?: string,
): LineStation[] {
  const ordered = applyFlowLayout(
    lineId,
    machines.filter((m) => machineSn(m) !== ""),
  );

  const groups: Machine[][] = [];
  const indexOfGroup = new Map<number, number>();
  for (const m of ordered) {
    const g = m.flowGroup ?? 0;
    let i = indexOfGroup.get(g);
    if (i === undefined) {
      i = groups.length;
      indexOfGroup.set(g, i);
      groups.push([]);
    }
    groups[i].push(m);
  }

  const chosen =
    outputMachineId && ordered.some((m) => machineSn(m) === outputMachineId)
      ? outputMachineId
      : undefined;
  const lastGroup = ordered.length
    ? Math.max(...ordered.map((m) => m.flowGroup ?? 0))
    : 0;

  return groups.flatMap((group, stage) =>
    group.map((m, i) => {
      const sn = machineSn(m);
      const position =
        group.length > 1
          ? `${stage + 1}${String.fromCharCode(97 + (i % 26))}`
          : `${stage + 1}`;
      const name = m.machineName || `${m.bautyp || m.machineType || ""} (${sn})`.trim();
      return {
        sn,
        name,
        position,
        stage,
        label: `${position} · ${name}`,
        isOutput: chosen ? sn === chosen : (m.flowGroup ?? 0) === lastGroup,
        type: m.bautyp || m.machineType || "",
      };
    }),
  );
}

// ------------------------------------------------------------- responses

/** A cause, status or speed range and its value. */
export interface NamedValue {
  name: string;
  value: number;
}

/** The total of a machine and its parts, largest first. */
export interface Totals {
  total: number;
  parts: NamedValue[];
}

const num = (v: unknown): number => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : 0;
};

/** The statement's payload: `{ results }` or the results themselves. */
const resultsOf = (data: unknown): unknown =>
  data && typeof data === "object" && "results" in (data as object)
    ? (data as { results: unknown }).results
    : data;

/** `[{ name, value }]` or `{ name: value }` as NamedValues. */
function namedValues(v: unknown): NamedValue[] {
  if (Array.isArray(v)) {
    return v
      .filter((x) => x && typeof x === "object")
      .map((x) => ({
        name: String((x as { name?: unknown }).name ?? "Unknown"),
        value: num((x as { value?: unknown }).value),
      }));
  }
  if (v && typeof v === "object") {
    return Object.entries(v as Record<string, unknown>)
      .filter(([, value]) => typeof value === "number" || typeof value === "string")
      .map(([name, value]) => ({ name, value: num(value) }));
  }
  return [];
}

function totalsOf(values: Map<string, number>): Totals {
  const parts = [...values.entries()]
    .map(([name, value]) => ({ name, value }))
    .filter((p) => p.value > 0)
    .sort((a, b) => b.value - a.value);
  return { total: parts.reduce((s, p) => s + p.value, 0), parts };
}

function add(values: Map<string, number>, items: NamedValue[]) {
  for (const { name, value } of items) values.set(name, (values.get(name) ?? 0) + value);
}

/** "2026-11-01 A" → ["2026-11-01", "A"] (the statements build it as CONCAT(date, ' ', shift)). */
export function splitCategory(category: string): [string, string] {
  const i = category.indexOf(" ");
  return i > 0 ? [category.slice(0, i), category.slice(i + 1)] : [category, ""];
}

const selected = (shift: string, shifts: string[]) =>
  shifts.length === 0 || shifts.includes(shift);

/**
 * PC.downtime_accumulated: `{ int: [{ name, value }], ext: [...] }` (ms).
 * `int` is the machine's own downtime, `ext` waiting for other machines.
 */
export function downtimeOf(data: unknown): { own: Totals; waiting: Totals } {
  let r = resultsOf(data) as Record<string, unknown> | null;
  if (r && typeof r === "object" && "downtime_accumulated" in r) {
    r = r.downtime_accumulated as Record<string, unknown>;
  }
  // older responses grouped everything under "undefined"
  if (r && typeof r === "object" && !r.int && !r.ext && Array.isArray(r["undefined"])) {
    r = { int: r["undefined"], ext: [] };
  }
  const own = new Map<string, number>();
  const waiting = new Map<string, number>();
  if (r && typeof r === "object") {
    add(own, namedValues(r.int));
    add(waiting, namedValues(r.ext));
  }
  return { own: totalsOf(own), waiting: totalsOf(waiting) };
}

/**
 * Results keyed by "YYYY-MM-DD SHIFT" with `[{ name, value }]` or
 * `{ name: value }` (PC.downtime_duration_of_faults, PC.machine_status_per_shift,
 * PC.average_production_speed), summed over the selected shifts.
 */
export function totalsByShiftCategory(data: unknown, shifts: string[]): Totals {
  const values = new Map<string, number>();
  const r = resultsOf(data);
  if (r && typeof r === "object" && !Array.isArray(r)) {
    for (const [category, items] of Object.entries(r as Record<string, unknown>)) {
      if (category === "status" || category === "error") continue;
      const [, shift] = splitCategory(category);
      if (!shift || !selected(shift, shifts)) continue;
      add(values, namedValues(items));
    }
  }
  return totalsOf(values);
}

/**
 * PC.number_of_rejects / PC.number_of_reject_events:
 * `{ "<date> 00:00:00 +0000 UTC": { "<shift>": { "<text>": count } } }`,
 * summed over the selected shifts.
 */
export function totalsByDateShift(data: unknown, shifts: string[]): Totals {
  const values = new Map<string, number>();
  const r = resultsOf(data);
  if (r && typeof r === "object" && !Array.isArray(r)) {
    for (const byShift of Object.values(r as Record<string, unknown>)) {
      if (!byShift || typeof byShift !== "object") continue;
      for (const [shift, items] of Object.entries(byShift as Record<string, unknown>)) {
        if (selected(shift, shifts)) add(values, namedValues(items));
      }
    }
  }
  return totalsOf(values);
}

/** PC.number_of_faults: `[{ text, total_count }]` (the 10 most frequent faults). */
export function totalsOfFaultRows(data: unknown): Totals {
  const values = new Map<string, number>();
  const r = resultsOf(data);
  const rows = Array.isArray(r)
    ? r
    : r && typeof r === "object" && Array.isArray((r as { number_of_faults?: unknown }).number_of_faults)
      ? (r as { number_of_faults: unknown[] }).number_of_faults
      : [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const { text, total_count } = row as { text?: unknown; total_count?: unknown };
    add(values, [{ name: String(text ?? "Unknown"), value: num(total_count) }]);
  }
  return totalsOf(values);
}

/**
 * Shift-grouped results `{ "<shift>": { "<timestamp>": value | { field } } }`
 * (PC.reject_ratio, PC.mtbf) as one value per "YYYY-MM-DD SHIFT", for the
 * selected shifts. The timestamp is the shift start; its date part names
 * the shift's day.
 */
export function valuesPerShift(
  data: unknown,
  shifts: string[],
  field?: string,
): Map<string, number> {
  const out = new Map<string, number>();
  const r = resultsOf(data);
  if (!r || typeof r !== "object" || Array.isArray(r)) return out;
  for (const [shift, byTime] of Object.entries(r as Record<string, unknown>)) {
    if (!byTime || typeof byTime !== "object" || !selected(shift, shifts)) continue;
    for (const [timestamp, v] of Object.entries(byTime as Record<string, unknown>)) {
      const raw =
        v && typeof v === "object"
          ? field
            ? (v as Record<string, unknown>)[field]
            : undefined
          : v;
      if (raw === null || raw === undefined || raw === "") continue;
      out.set(`${timestamp.slice(0, 10)} ${shift}`, num(raw));
    }
  }
  return out;
}

/** Categories "YYYY-MM-DD SHIFT" by date, then shift. */
export const compareCategories = (a: string, b: string): number => {
  const [da, sa] = splitCategory(a);
  const [db, sb] = splitCategory(b);
  return da === db ? sa.localeCompare(sb) : da.localeCompare(db);
};
